import secureStorage from './secureStorage';

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  retryAfterMs?: number;
}

interface RequestRecord {
  count: number;
  resetTime: number;
  blocked: boolean;
  lastRequestTime: number;
}

class RateLimiter {
  private requests = new Map<string, RequestRecord>();
  private configs = new Map<string, RateLimitConfig>();

  constructor() {
    // Default rate limit configurations
    this.setRateLimit('default', { maxRequests: 100, windowMs: 60000 }); // 100 requests per minute
    this.setRateLimit('auth', { maxRequests: 20, windowMs: 60000, retryAfterMs: 10000 }); // 20 auth requests per minute, 10 second retry
    this.setRateLimit('upload', { maxRequests: 10, windowMs: 60000, retryAfterMs: 60000 }); // 10 uploads per minute
    this.setRateLimit('search', { maxRequests: 30, windowMs: 60000 }); // 30 searches per minute
  }

  setRateLimit(endpoint: string, config: RateLimitConfig): void {
    this.configs.set(endpoint, config);
  }

  private getKey(endpoint: string, identifier?: string): string {
    // Use a combination of endpoint and user identifier (IP would be ideal but not available in client)
    // Must use secureStorage to properly decrypt the user data
    const userData = secureStorage.getUserData();
    const userId = userData?.userId ?? 'anonymous';
    return `${endpoint}_${identifier || userId || 'unknown'}`;
  }

  private getConfig(endpoint: string): RateLimitConfig {
    return this.configs.get(endpoint) || this.configs.get('default')!;
  }

  isAllowed(endpoint: string, identifier?: string): { allowed: boolean; retryAfter?: number; remaining?: number } {
    const key = this.getKey(endpoint, identifier);
    const config = this.getConfig(endpoint);
    const now = Date.now();

    let record = this.requests.get(key);

    // Initialize or reset if window has passed
    if (!record || now >= record.resetTime) {
      record = {
        count: 0,
        resetTime: now + config.windowMs,
        blocked: false,
        lastRequestTime: 0
      };
    }

    // Check if currently blocked and retry period hasn't passed
    if (record.blocked && config.retryAfterMs) {
      const blockedUntil = record.lastRequestTime + config.retryAfterMs;
      if (now < blockedUntil) {
        return {
          allowed: false,
          retryAfter: Math.ceil((blockedUntil - now) / 1000)
        };
      } else {
        // Unblock after retry period
        record.blocked = false;
        record.count = 0;
        record.resetTime = now + config.windowMs;
      }
    }

    // Check rate limit
    if (record.count >= config.maxRequests) {
      record.blocked = true;
      record.lastRequestTime = now;
      this.requests.set(key, record);
      
      return {
        allowed: false,
        retryAfter: config.retryAfterMs ? Math.ceil(config.retryAfterMs / 1000) : Math.ceil((record.resetTime - now) / 1000)
      };
    }

    // Allow request and increment counter
    record.count++;
    record.lastRequestTime = now;
    this.requests.set(key, record);

    return {
      allowed: true,
      remaining: config.maxRequests - record.count
    };
  }

  // Clean up old records periodically
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.requests.forEach((record, key) => {
      if (now >= record.resetTime && !record.blocked) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => {
      this.requests.delete(key);
    });
  }

  // Reset rate limits for a specific endpoint/user (admin function)
  reset(endpoint: string, identifier?: string): void {
    const key = this.getKey(endpoint, identifier);
    this.requests.delete(key);
  }

  // Get current status for a specific endpoint/user
  getStatus(endpoint: string, identifier?: string): { count: number; remaining: number; resetTime: number; blocked: boolean } {
    const key = this.getKey(endpoint, identifier);
    const config = this.getConfig(endpoint);
    const record = this.requests.get(key);

    if (!record) {
      return {
        count: 0,
        remaining: config.maxRequests,
        resetTime: Date.now() + config.windowMs,
        blocked: false
      };
    }

    return {
      count: record.count,
      remaining: Math.max(0, config.maxRequests - record.count),
      resetTime: record.resetTime,
      blocked: record.blocked
    };
  }
}

// Create singleton instance
export const rateLimiter = new RateLimiter();

// Clean up every 5 minutes
setInterval(() => {
  rateLimiter.cleanup();
}, 5 * 60 * 1000);

export default rateLimiter;